import { useEffect, useState } from 'react';
import { Minus, Plus } from 'lucide-react';

import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { useCheckPermission } from '@/hooks/useCheckPermission';
import { formatAmount, netOrderTotal, orderCurrency, remainingQuantity } from './order-totals';

/**
 * Line-level confirmation for a paid Click & Collect order (NEA-6025).
 * Functional placeholder built from the local ui primitives — design polish
 * comes later. Two paths out:
 *   • "Approve all" hands the order back to the parent, which reuses the
 *     existing confirm flow (email modal → order update).
 *   • "Partial refund" switches to selectable mode — per line a checkbox +
 *     quantity stepper clamped to 1..(quantity − alreadyRefunded) — and
 *     submits `[{ barcode, quantity }]` via `onSubmitRefund`. Execution is
 *     asynchronous (stream handler), so the parent shows a processing state.
 *
 * `initialMode="refund"` opens straight into the selectable view (for orders
 * that are already confirmed / partially refunded, where "Approve all" is
 * moot) — the back button then closes the modal instead of returning to the
 * confirm view.
 */
export function OrderConfirmModal({ order, shop, open, onOpenChange, onApproveAll, onSubmitRefund, initialMode = 'confirm' }) {
	const canManage = useCheckPermission('org:orders:manage');
	const [mode, setMode] = useState(initialMode);
	const [selected, setSelected] = useState({});
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		if (open) {
			setMode(initialMode);
			setSelected({});
			setSubmitting(false);
		}
	}, [open, order?.id, initialMode]);

	if (!order) return null;

	const products = order.products || [];
	const currency = orderCurrency(order);
	const orderRef = order.pickupReference || order.id;
	const permissionTooltip = canManage ? undefined : "You don't have permission to manage orders";

	const toggleLine = (product) => {
		setSelected((prev) => {
			const next = { ...prev };
			if (next[product.barcode]) {
				delete next[product.barcode];
			} else {
				next[product.barcode] = 1;
			}
			return next;
		});
	};

	const stepLine = (product, delta) => {
		setSelected((prev) => {
			const current = prev[product.barcode];
			if (!current) return prev;
			const next = Math.min(remainingQuantity(product), Math.max(1, current + delta));
			return { ...prev, [product.barcode]: next };
		});
	};

	const items = products.filter((p) => selected[p.barcode]).map((p) => ({ barcode: p.barcode, quantity: selected[p.barcode] }));
	const refundTotal = products.reduce((sum, p) => sum + (Number(p.price) || 0) * (selected[p.barcode] || 0), 0);

	const submitRefund = async () => {
		if (!items.length || submitting) return;
		setSubmitting(true);
		try {
			await onSubmitRefund?.(items);
			// Defer the close so Radix unwinds body pointer-events cleanly —
			// same pattern as OrderUpdateModal. Only close on success; a
			// failure keeps the modal open so the retailer can retry.
			setTimeout(() => onOpenChange?.(false), 0);
		} catch {
			// The parent toasts and re-throws — swallow so the rejection
			// doesn't bubble to the click handler unhandled.
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="rounded-2xl sm:max-w-lg">
				<div className="text-sm text-muted-foreground">Unify by NearSt · {shop?.name}</div>
				<DialogTitle className="mt-1 text-2xl font-extrabold tracking-tight">
					{mode === 'confirm' ? `Confirm order #${orderRef}` : `Partial refund — order #${orderRef}`}
				</DialogTitle>
				<DialogDescription className="text-sm text-muted-foreground">
					{mode === 'confirm'
						? 'Check the items below, then approve the whole order or refund part of it.'
						: 'Select the items and quantities to refund. The customer keeps the rest of the order.'}
				</DialogDescription>

				{mode === 'confirm' ? (
					<div className="space-y-2 pt-1">
						{products.map((p, i) => (
							<div key={p.barcode || i} className="flex items-baseline justify-between gap-3">
								<div className="text-sm font-semibold text-foreground">
									{Number(p.quantity) || 1} x {p.name || 'Item'}
									{Number(p.refundedQuantity) > 0 ? (
										<span className="ml-2 text-xs font-medium text-muted-foreground">
											{p.refundedQuantity} refunded
										</span>
									) : null}
								</div>
								<div className="text-sm font-semibold tabular-nums text-foreground">
									{formatAmount((Number(p.price) || 0) * remainingQuantity(p), currency)}
								</div>
							</div>
						))}
						<div className="flex items-baseline justify-between border-t border-border/60 pt-2">
							<div className="text-sm font-bold text-foreground">Total</div>
							<div className="text-sm font-bold tabular-nums text-foreground">
								{formatAmount(netOrderTotal(order), currency)}
							</div>
						</div>
					</div>
				) : (
					<div className="space-y-2 pt-1">
						{products.map((p, i) => {
							const remaining = remainingQuantity(p);
							const qty = selected[p.barcode] || 0;
							return (
								<div key={p.barcode || i} className="flex items-start justify-between gap-3">
									<label className="flex min-w-0 flex-1 cursor-pointer items-start gap-2 text-sm font-medium text-foreground">
										<input
											type="checkbox"
											data-testid={`refund-select-${p.barcode}`}
											checked={!!qty}
											disabled={remaining === 0}
											onChange={() => toggleLine(p)}
											className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-input text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
										/>
										<span className="min-w-0">
											<span
												data-testid={`refund-name-${p.barcode}`}
												title={p.name}
												className="line-clamp-2 break-words"
											>
												{p.name || 'Item'}
											</span>
											<span
												data-testid={`refund-meta-${p.barcode}`}
												className="block text-xs font-normal text-muted-foreground"
											>
												{p.barcode ? <span className="tabular-nums">{p.barcode} · </span> : null}
												<span>{remaining === 0 ? 'fully refunded' : `${remaining} available`}</span>
											</span>
										</span>
									</label>
									{qty ? (
										<div className="flex shrink-0 items-center gap-2">
											<button
												type="button"
												data-testid={`refund-decrease-${p.barcode}`}
												aria-label={`Decrease refund quantity for ${p.name}`}
												disabled={qty <= 1}
												onClick={() => stepLine(p, -1)}
												className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-muted text-foreground transition hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-50"
											>
												<Minus className="h-3.5 w-3.5" />
											</button>
											<span
												data-testid={`refund-qty-${p.barcode}`}
												className="w-5 text-center text-sm font-semibold tabular-nums"
											>
												{qty}
											</span>
											<button
												type="button"
												data-testid={`refund-increase-${p.barcode}`}
												aria-label={`Increase refund quantity for ${p.name}`}
												disabled={qty >= remaining}
												onClick={() => stepLine(p, 1)}
												className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-muted text-foreground transition hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-50"
											>
												<Plus className="h-3.5 w-3.5" />
											</button>
											<span className="w-16 text-right text-sm font-semibold tabular-nums text-foreground">
												{formatAmount((Number(p.price) || 0) * qty, currency)}
											</span>
										</div>
									) : null}
								</div>
							);
						})}
						<div className="flex items-baseline justify-between border-t border-border/60 pt-2">
							<div className="text-sm font-bold text-foreground">Refund total</div>
							<div data-testid="refund-total" className="text-sm font-bold tabular-nums text-foreground">
								{formatAmount(refundTotal, currency)}
							</div>
						</div>
					</div>
				)}

				<div className="flex justify-end gap-2 pt-3">
					{mode === 'confirm' ? (
						<>
							<button
								type="button"
								onClick={() => onOpenChange?.(false)}
								className="rounded-full bg-muted px-4 py-2 text-sm font-semibold text-foreground transition hover:bg-secondary"
							>
								Close
							</button>
							<button
								type="button"
								data-testid="confirm-partial-refund"
								disabled={!canManage}
								title={permissionTooltip}
								onClick={() => setMode('refund')}
								className="rounded-full bg-muted px-4 py-2 text-sm font-semibold text-foreground transition hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-50"
							>
								Partial refund
							</button>
							<button
								type="button"
								data-testid="confirm-approve-all"
								disabled={!canManage}
								title={permissionTooltip}
								onClick={() => onApproveAll?.(order)}
								className="rounded-full bg-primary px-6 py-2 text-sm font-semibold text-primary-foreground transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
							>
								Approve all
							</button>
						</>
					) : (
						<>
							<button
								type="button"
								data-testid="refund-back"
								onClick={() => (initialMode === 'refund' ? onOpenChange?.(false) : setMode('confirm'))}
								className="rounded-full bg-muted px-4 py-2 text-sm font-semibold text-foreground transition hover:bg-secondary"
							>
								{initialMode === 'refund' ? 'Close' : 'Back'}
							</button>
							<button
								type="button"
								data-testid="submit-refund"
								disabled={!canManage || submitting || !items.length}
								title={permissionTooltip}
								onClick={submitRefund}
								className="rounded-full bg-primary px-6 py-2 text-sm font-semibold text-primary-foreground transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
							>
								Refund {formatAmount(refundTotal, currency)}
							</button>
						</>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}
